import { getBeforeSend } from './beforeSend/getBeforeSend';
import { isDatadogCategoryEnabled } from './isDatadogCategoryEnabled';
import { datadogRum } from '@datadog/browser-rum';
import { v4 } from 'uuid';

export type DatadogRumUser = {
  id: string;
  name?: string;
  email?: string;
};

type TrackingConsent = 'granted' | 'not-granted';

const checkUrlAllowed = (url: string) => {
  const loc = new URL(url);
  return (
    loc.protocol.toLowerCase() === 'https:' &&
    (loc.hostname.endsWith('aws.tfcloud.corp') ||
      loc.hostname.endsWith('aws.thrivent.com'))
  );
};

export function initializeDatadogRum({
  applicationId,
  clientToken,
  defaultPrivacyLevel = 'mask',
  env,
  service,
  sessionReplaySampleRate = 0, // Secure baseline: Do not capture user replay data in DataDog: https://thrivent.atlassian.net/browse/DOP-360
  trackingConsent = 'not-granted',
  trackViewsManually = false,
  version,
}: {
  applicationId: string;
  clientToken: string;
  env: string;
  service: string;
  version: string;
  defaultPrivacyLevel?: 'mask' | 'mask-user-input';
  sessionReplaySampleRate?: number;
  trackingConsent?: TrackingConsent;
  trackViewsManually?: boolean;
}): void {
  const isInIFrame =
    typeof window !== 'undefined' &&
    window?.location !== window?.parent?.location;

  if (isInIFrame) {
    // Do not initialize RUM when loading inside an iframe as this causes Views of these
    // pages to show up in user journeys
    // See: https://docs.icweb.stage.aws.tfcloud.corp/docs/adr/removing-iframe-pages-as-views
    return;
  }

  datadogRum.init({
    trackingConsent,
    allowedTracingUrls: [
      { match: checkUrlAllowed, propagatorTypes: ['tracecontext'] },
    ],
    applicationId,
    beforeSend: getBeforeSend(),
    clientToken,
    defaultPrivacyLevel,
    enableExperimentalFeatures: ['feature_flags'],
    enablePrivacyForActionName: defaultPrivacyLevel === 'mask',
    env,
    excludedActivityUrls: [
      // Exclude health/liveliness checks
      /\/mgmt\/health\/liveness$/,
      /\/mgmt\/health\/readiness$/,
      /\/metrics$/,
    ],
    proxy: 'https://ddrp.apps.thrivent.com',
    service,
    sessionReplaySampleRate,
    trackSessionAcrossSubdomains: true,
    version,
    trackViewsManually,
  });
}

export function setDefaultRumGlobalContext(): void {
  if (datadogRum.getInitConfiguration() === undefined) {
    return;
  }
  const service = datadogRum.getInitConfiguration()?.service;
  datadogRum.setGlobalContextProperty('tabId', v4());
  datadogRum.setGlobalContextProperty('service', service);
  datadogRum.setGlobalContextProperty('thrivent.team', service);
}

export function setRumUser(datadogUser: DatadogRumUser): void {
  if (datadogRum.getInitConfiguration() === undefined) {
    return;
  }
  datadogRum.setUser(datadogUser);
}

export function setGlobalContextFeatureFlags(
  flags: Record<string, string | number | boolean>,
): void {
  if (datadogRum.getInitConfiguration() === undefined) {
    return;
  }
  datadogRum.setGlobalContextProperty('feature_flags', flags);
}

/**
 * Returns the tracking consent for an application based on use of OneTrust
 * and current OneTrust active groups
 *
 * @returns TrackingConsent Whether tracking consent has been granted or not
 */
export function getOneTrustTrackingConsent(): TrackingConsent {
  if (isDatadogCategoryEnabled()) {
    return 'granted';
  }
  return 'not-granted';
}

/**
 * Adds event listener to window to check for tracking consent based on OneTrust
 * any time OneTrust active groups are updated
 *
 */
export function addOneTrustTrackingConsentEventListener(): void {
  if (typeof window !== 'undefined') {
    window.addEventListener('OneTrustGroupsUpdated', () => {
      datadogRum.setTrackingConsent(getOneTrustTrackingConsent());
    });
  }
}

/**
 * Adds feature flag evaluations to a RUM session based on provided
 * flag name value pairs.
 *
 * @param featureFlags Key value pair of flag name and flag value
 */
export function addFeatureFlagEvaluation(
  featureFlags: Record<string, string | number | boolean> | undefined,
): void {
  if (featureFlags !== undefined) {
    Object.entries(featureFlags).map(([key, value]) =>
      datadogRum.addFeatureFlagEvaluation(key, value),
    );
  }
}
